import {
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
  TextOutlinedInput,
} from '@octano/global-ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../../../locales/translations';
import { AssignedStudentsPaginationQuery } from '../types';
import { useAssignedStudentFiltersLoader } from './AssignedStudentsBaseLoader';

export interface FormData {
  identification?: string;
  names?: string;
  identificationType: (OutlinedSelectOptionType & { value: string }) | null;
  school?: (OutlinedSelectOptionType & { value: number }) | null;
  studyPlan?: (OutlinedSelectOptionType & { value: number }) | null;
  campus?: (OutlinedSelectOptionType & { value: number }) | null;
  schedule?: (OutlinedSelectOptionType & { value: number }) | null;
}

export default function AssignedStudentFilters({
  onSubmit,
}: {
  onSubmit: (data: Partial<AssignedStudentsPaginationQuery>) => void;
}) {
  const { t } = useTranslation(
    TranslationsKeys.TEACHER_MANAGMENT_TEACHER_FILE_ASSIGNED_STUDENTS,
  );
  const { data } = useAssignedStudentFiltersLoader();
  const {
    identificationTypes,
    schools,
    studyPlanVersions,
    campuses,
    schedules,
  } = data!;
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<FormData>();

  const localSubmit = (data: FormData) => {
    const noEmptyStrings = (str?: string) =>
      str && str.trim() !== '' ? str.trim() : undefined;
    onSubmit({
      identificationType: data.identificationType?.value,
      identification: noEmptyStrings(data.identification),
      names: noEmptyStrings(data.names),
      school: data.school?.value,
      studyPlanVersion: data.studyPlan?.value,
      campus: data.campus?.value,
      schedule: data.schedule?.value,
    });
  };

  return (
    <Form
      onSubmit={handleSubmit(localSubmit)}
      className="g-table-container mt-2 pb-4 pt-4"
    >
      <Row className="pt-5">
        <Col lg={4} className="pl-0">
          <OutlinedSelect
            name="identificationType"
            control={control}
            label={t('filters.identificationType')}
            isClearable
            options={identificationTypes.map((type) => ({
              value: type.id,
              label: type.name,
            }))}
          />
        </Col>
        <Col lg={4} className="pl-0">
          <TextOutlinedInput
            name="identification"
            control={control}
            label={t('filters.identification')}
            placeholder={t('filters.identificationPlaceholder')}
          />
        </Col>
        <Col lg={4} className="pl-0">
          <TextOutlinedInput
            name="names"
            control={control}
            label={t('filters.names')}
            placeholder={t('filters.namesPlaceholder')}
          />
        </Col>
        <Col lg={4} className="pl-0">
          <OutlinedSelect
            name="school"
            control={control}
            label={t('filters.school')}
            placeholder={t('filters.schoolPlaceholder')}
            isClearable
            options={schools.map((school) => ({
              value: school.id,
              label: school.name,
            }))}
          />
        </Col>
        <Col lg={4} className="pl-0">
          <OutlinedSelect
            name="studyPlan"
            control={control}
            label={t('filters.studyPlanVersion')}
            placeholder={t('filters.studyPlanVersionPlaceholder')}
            isClearable
            options={studyPlanVersions.map((studyPlan) => ({
              value: studyPlan.id,
              label: studyPlan.name,
            }))}
          />
        </Col>

        <Col lg={4} className="pl-0">
          <OutlinedSelect
            name="campus"
            control={control}
            label={t('filters.campus')}
            placeholder={t('filters.campusPlaceholder')}
            isClearable
            options={campuses.map((campus) => ({
              value: campus.id,
              label: campus.name,
            }))}
          />
        </Col>
        <Col lg={4} className="pl-0">
          <OutlinedSelect
            name="schedule"
            control={control}
            label={t('filters.schedule')}
            placeholder={t('filters.schedulePlaceholder')}
            isClearable
            options={schedules.map((schedule) => ({
              value: schedule.id,
              label: schedule.name,
            }))}
          />
        </Col>
        <Col xs={12} md={8}>
          <div className="d-flex justify-content-end mt-4">
            <Button
              type="submit"
              color="primary"
              className="mb-3"
              size="md"
              disabled={isSubmitting}
              text={t('filters.search')}
            />
            <Button
              type="submit"
              color="primary"
              className="mb-3 ml-2"
              onClick={() => {
                reset({
                  identificationType: null,
                  identification: undefined,
                  names: undefined,
                  school: null,
                  studyPlan: null,
                  campus: null,
                  schedule: null,
                });
              }}
              size="md"
              text={t('filters.clean')}
            />
          </div>
        </Col>
      </Row>
    </Form>
  );
}
